import { isConvex, orderQuad, quadArea, type Point, type Quad } from './geometry.js';

/**
 * SCAN-002 — finding the page in the picture.
 *
 * The job is to answer, ten times a second on a phone, where the four corners
 * of the sheet of paper are. Every mature scanner does this with OpenCV, which
 * is eight megabytes of WebAssembly to download before the camera can open —
 * more than the rest of the application put together, on the one screen most
 * likely to be reached on mobile data. So the pipeline is written out here
 * instead. It is about two hundred lines because the only parts of OpenCV it
 * needs are the four oldest ideas in the subject.
 *
 * Blur, then Sobel, gives an image of where the brightness changes. A page on a
 * desk has its strongest changes along its own four edges, so a Hough transform
 * — which asks every edge pixel to vote for the lines it could lie on — finds
 * those four as its loudest answers. Two roughly-parallel pairs, intersected,
 * are the corners.
 *
 * It works on a deliberately small copy of the frame: at this size a page edge
 * is still tens of pixels long and unmistakable, while the pattern printed on
 * the page has blurred into nothing and stopped voting. Detecting on the full
 * twelve megapixels would be slower and *worse*.
 */

/** Where the detector does its thinking, whatever size the camera gives us. */
export const DETECT_WIDTH = 240;

/** One vote per degree. Finer bins split a single edge's votes between them. */
const THETA_STEPS = 180;

/**
 * The most of a frame that may be called edge, however contrasty it is.
 *
 * Not the definition of an edge — that is `edgeThreshold`'s job — but a ceiling
 * on the work. Every edge pixel votes for a fan of lines, so a frame of gravel
 * would otherwise cost ten times what a frame of desk does, on the device least
 * able to afford it.
 */
const MAX_EDGE_FRACTION = 0.2;

/** Below this the frame is a wall, a hand, or a lens cap. Nothing to find. */
const MIN_EDGE_PIXELS = 200;

/**
 * How far either side of its own gradient a pixel is allowed to vote, in bins
 * of one degree.
 *
 * A pixel sitting on a straight edge has a gradient square across that edge,
 * and the only lines it can be evidence for are the ones running that way. The
 * window is the room left for the ways that goes wrong: a blurred edge, a
 * corner where two edges meet, a letter's stroke a pixel wide. Twenty-two
 * degrees is comfortably wider than the error a real page edge shows and still
 * four times narrower than voting blind.
 */
const DIRECTION_WINDOW = 22;

/** How far from the strongest line's angle another line can be and still be
 *  called one of its parallels. A page photographed at a plausible angle keeps
 *  its opposite edges inside this; beyond it, they are two different things. */
const FAMILY_DEGREES = 28;

/** Cosine and sine of every bin's angle. The bins never change, so neither do
 *  these, and a frame that rebuilt them would pay for a hundred and eighty
 *  sines it already had. */
const COS = new Float32Array(THETA_STEPS);
const SIN = new Float32Array(THETA_STEPS);
for (let t = 0; t < THETA_STEPS; t += 1) {
  COS[t] = Math.cos((t * Math.PI) / THETA_STEPS);
  SIN[t] = Math.sin((t * Math.PI) / THETA_STEPS);
}

interface Line {
  /** Radians, in the same [0, π) convention the accumulator is binned in. */
  theta: number;
  /** Distance from the frame's centre, signed. */
  rho: number;
  votes: number;
}

/**
 * Brightness only.
 *
 * The coefficients are the usual perceptual ones. Colour would not help: a page
 * is found by its outline, and an outline is a change in brightness whatever
 * colour either side of it happens to be.
 */
export function toGray(rgba: Uint8ClampedArray, out?: Uint8Array): Uint8Array {
  const gray = out ?? new Uint8Array(rgba.length / 4);
  for (let i = 0, p = 0; i < rgba.length; i += 4, p += 1) {
    gray[p] = ((rgba[i] as number) * 77 + (rgba[i + 1] as number) * 150 + (rgba[i + 2] as number) * 29) >> 8;
  }
  return gray;
}

/**
 * A 3-wide box blur, run separately across and then down.
 *
 * Sobel on an unblurred frame answers with the paper's grain and the desk's
 * wood, which vote as loudly as the edge does and drown it. Two cheap passes
 * are enough to quiet them: the page's edge is a step tens of pixels long and
 * survives, and the texture is one pixel wide and does not.
 */
function blur(src: Uint8Array, width: number, height: number, pass: Uint8Array, out: Uint8Array): Uint8Array {
  for (let y = 0; y < height; y += 1) {
    const row = y * width;
    for (let x = 0; x < width; x += 1) {
      const left = src[row + (x > 0 ? x - 1 : 0)] as number;
      const right = src[row + (x < width - 1 ? x + 1 : width - 1)] as number;
      pass[row + x] = ((left + (src[row + x] as number) * 2 + right) / 4) | 0;
    }
  }

  for (let y = 0; y < height; y += 1) {
    const up = (y > 0 ? y - 1 : 0) * width;
    const down = (y < height - 1 ? y + 1 : height - 1) * width;
    const row = y * width;
    for (let x = 0; x < width; x += 1) {
      out[row + x] = (((pass[up + x] as number) + (pass[row + x] as number) * 2 + (pass[down + x] as number)) / 4) | 0;
    }
  }

  return out;
}

interface Gradient {
  /** |gx| + |gy| — the sum stands in for the hypotenuse it is within 8% of,
   *  and this runs on every pixel of every frame. */
  magnitude: Uint16Array;
  /**
   * How steeply the brightness climbs across and down, kept rather than thrown
   * away.
   *
   * These two are the answer to the question the vote below would otherwise ask
   * a hundred and eighty times over: a pixel on an edge can only lie on lines
   * running across its own gradient, and Sobel has already worked out which way
   * that is. Kept as the raw pair rather than as an angle because the angle is
   * wanted only for the pixels that turn out to be edges — a fifth of them at
   * most — and an arctangent is dear enough to be worth asking for only there.
   */
  gx: Int16Array;
  gy: Int16Array;
}

function sobel(src: Uint8Array, width: number, height: number, into: Gradient): Gradient {
  const { magnitude, gx: gxOut, gy: gyOut } = into;
  for (let y = 1; y < height - 1; y += 1) {
    for (let x = 1; x < width - 1; x += 1) {
      const i = y * width + x;
      const tl = src[i - width - 1] as number;
      const tc = src[i - width] as number;
      const tr = src[i - width + 1] as number;
      const ml = src[i - 1] as number;
      const mr = src[i + 1] as number;
      const bl = src[i + width - 1] as number;
      const bc = src[i + width] as number;
      const br = src[i + width + 1] as number;
      const gx = tr + 2 * mr + br - tl - 2 * ml - bl;
      const gy = bl + 2 * bc + br - tl - 2 * tc - tr;
      magnitude[i] = Math.abs(gx) + Math.abs(gy);
      gxOut[i] = gx;
      gyOut[i] = gy;
    }
  }
  // The one-pixel border is never written and never read: it is left at the
  // zero it was allocated with, which is what makes these buffers safe to hand
  // back to the next frame unwiped.
  return into;
}

/**
 * The gradient strength above which a pixel counts as an edge, chosen per
 * frame rather than fixed.
 *
 * A fixed threshold is a promise about the lighting, and the lighting is the
 * one thing a photograph of paper never holds still: white paper on a dark desk
 * under a lamp and grey paper on a pale desk in a hallway differ by more than
 * any constant survives.
 *
 * Otsu's method asks the only question that travels between those two rooms —
 * where does this frame's own histogram split into "flat" and "edge"? — and
 * answers it without being told anything about the scene.
 *
 * Keeping a fixed *share* of the pixels instead, which is the cheaper way to be
 * lighting-independent, is what this replaced, and it failed on the document it
 * most needed to work on. A page covered in print has far more strong gradient
 * in its text than around its border, so "the strongest eight per cent" was
 * entirely text: the four edges of the page fell below the line, and a full
 * sheet of A4 was reported as no page at all.
 */
function edgeThreshold(magnitude: Uint16Array): number {
  const histogram = new Int32Array(256);
  for (let i = 0; i < magnitude.length; i += 1) {
    const bucket = (magnitude[i] as number) >> 2;
    histogram[bucket] = (histogram[bucket] as number) + 1;
  }
  const total = magnitude.length;

  let weightedSum = 0;
  for (let bucket = 0; bucket < 256; bucket += 1) weightedSum += bucket * (histogram[bucket] as number);

  let belowCount = 0;
  let belowSum = 0;
  let bestVariance = -1;
  let split = 0;
  for (let bucket = 0; bucket < 255; bucket += 1) {
    belowCount += histogram[bucket] as number;
    if (belowCount === 0) continue;
    const aboveCount = total - belowCount;
    if (aboveCount === 0) break;
    belowSum += bucket * (histogram[bucket] as number);
    const gap = belowSum / belowCount - (weightedSum - belowSum) / aboveCount;
    const variance = belowCount * aboveCount * gap * gap;
    if (variance > bestVariance) {
      bestVariance = variance;
      split = bucket;
    }
  }

  // The ceiling, applied only when Otsu's answer would let too much through.
  let kept = 0;
  let ceiling = 0;
  const allowed = Math.floor(total * MAX_EDGE_FRACTION);
  for (let bucket = 255; bucket > 0; bucket -= 1) {
    kept += histogram[bucket] as number;
    if (kept >= allowed) {
      ceiling = bucket;
      break;
    }
  }

  // 40 is the floor: below it the "edges" are the sensor's own noise, and a
  // frame of blank wall would otherwise vote itself a full set of lines.
  return Math.max(40, (Math.max(split, ceiling) + 1) << 2);
}

/** Angular distance between two headings, in degrees, over a half-turn. */
const angleGap = (a: number, b: number): number => {
  const d = Math.abs(a - b) % 180;
  return d > 90 ? 180 - d : d;
};

/**
 * Where two lines cross, or null if they are parallel enough not to.
 *
 * Both are in centre-relative coordinates; so is the answer, and the caller
 * puts the centre back.
 */
function intersect(a: Line, b: Line): Point | null {
  const sinA = Math.sin(a.theta);
  const cosA = Math.cos(a.theta);
  const sinB = Math.sin(b.theta);
  const cosB = Math.cos(b.theta);
  const det = cosA * sinB - sinA * cosB;
  if (Math.abs(det) < 1e-6) return null;
  return {
    x: (a.rho * sinB - b.rho * sinA) / det,
    y: (b.rho * cosA - a.rho * cosB) / det,
  };
}

/**
 * The lines the edge pixels agree on, strongest first.
 *
 * A single edge in the picture lights up a small blob of neighbouring bins
 * rather than one, so only the peak of each blob is reported — otherwise the
 * top of the list is the same page edge described eleven slightly different
 * ways, and the opposite edge never gets a look in.
 */
function houghLines(
  gradient: Gradient,
  width: number,
  height: number,
  threshold: number,
  accumulator: Int32Array,
): Line[] {
  const { magnitude, gx, gy } = gradient;
  const halfW = width / 2;
  const halfH = height / 2;
  const rhoLimit = Math.ceil(Math.hypot(halfW, halfH));
  const rhoBins = rhoLimit * 2 + 1;
  accumulator.fill(0);

  let edgePixels = 0;
  for (let y = 1; y < height - 1; y += 1) {
    const dy = y - halfH;
    for (let x = 1; x < width - 1; x += 1) {
      const i = y * width + x;
      if ((magnitude[i] as number) < threshold) continue;
      edgePixels += 1;
      const dx = x - halfW;

      /*
       * Which way this pixel's own edge runs, and the only lines it is allowed
       * to vote for.
       *
       * The arctangent is asked for here, on the pixels that turned out to be
       * edges, rather than in the gradient pass on all of them: at the
       * threshold this frame settled on, that is one call in five.
       */
      let heading = Math.round((Math.atan2(gy[i] as number, gx[i] as number) * THETA_STEPS) / Math.PI) % THETA_STEPS;
      if (heading < 0) heading += THETA_STEPS;

      // A vote for a line at sixty degrees to the pixel's own gradient is a
      // vote that the edge it sits on runs two ways at once. Casting a hundred
      // and eighty of them per pixel is where this used to spend nearly all of
      // its time.
      for (let step = -DIRECTION_WINDOW; step <= DIRECTION_WINDOW; step += 1) {
        let t = heading + step;
        if (t < 0) t += THETA_STEPS;
        else if (t >= THETA_STEPS) t -= THETA_STEPS;
        const rho = Math.round(dx * (COS[t] as number) + dy * (SIN[t] as number)) + rhoLimit;
        const bin = t * rhoBins + rho;
        accumulator[bin] = (accumulator[bin] as number) + 1;
      }
    }
  }
  if (edgePixels < MIN_EDGE_PIXELS) return [];

  // A page edge crossing the whole frame gets roughly one vote per pixel of its
  // length, so this asks for a line at least a fifth of the frame long.
  const minVotes = Math.max(20, Math.floor(Math.min(width, height) * 0.2));
  const lines: Line[] = [];

  for (let t = 0; t < THETA_STEPS; t += 1) {
    for (let r = 2; r < rhoBins - 2; r += 1) {
      const votes = accumulator[t * rhoBins + r] as number;
      if (votes < minVotes) continue;

      let peak = true;
      for (let dt = -2; dt <= 2 && peak; dt += 1) {
        for (let dr = -6; dr <= 6; dr += 1) {
          if (dt === 0 && dr === 0) continue;

          // Angle wraps, and wrapping mirrors the distance with it: a line
          // described as pointing 179° is the same line described as pointing
          // -1° from the other side, so the bin one step past the end of the
          // range is the bin at the other end with its rho reflected. Without
          // this every edge lying near the seam is found twice, and the two
          // copies then crowd out the page's opposite edge.
          let tt = t + dt;
          let rr = r + dr;
          if (tt < 0) {
            tt += THETA_STEPS;
            rr = rhoBins - 1 - rr;
          } else if (tt >= THETA_STEPS) {
            tt -= THETA_STEPS;
            rr = rhoBins - 1 - rr;
          }
          if (rr < 0 || rr >= rhoBins) continue;

          const neighbour = accumulator[tt * rhoBins + rr] as number;
          if (neighbour > votes) {
            peak = false;
            break;
          }
          // An edge a few pixels thick fills a run of bins with the identical
          // count, and a strict comparison calls every one of them a peak. The
          // tie goes to the first in scan order, so a plateau reports once.
          if (neighbour === votes && (tt < t || (tt === t && rr < r))) {
            peak = false;
            break;
          }
        }
      }
      if (!peak) continue;

      lines.push({ theta: (t * Math.PI) / THETA_STEPS, rho: r - rhoLimit, votes });
    }
  }

  // A line that is really in the picture is agreed on along most of its length.
  // A line that is an accident collects the same few votes spread thinly over
  // the whole frame, and that is what survives an unqualified vote count: a
  // hundred and twenty votes strung along 340 pixels of gravel looks, to the
  // accumulator, exactly like a real edge. Asking what share of the crossing
  // voted separates them without knowing anything about the scene.
  //
  // The cut sits where it does because it was measured rather than guessed. On
  // a frame of pure noise the best line manages 0.37; the four edges of a page
  // score between 0.64 and 0.76. Halfway between is the whole margin there is,
  // and it is a wide one.
  return lines
    .filter((line) => line.votes >= chordLength(line.theta, line.rho, halfW, halfH) * 0.45)
    .sort((a, b) => b.votes - a.votes)
    .slice(0, 60);
}

/**
 * How far a line travels inside the frame, in pixels.
 *
 * The line is walked as a point moving away from its closest approach to the
 * centre, and the walk is clipped to each pair of sides in turn; what is left
 * is the part inside. A line that misses the frame entirely returns zero.
 */
function chordLength(theta: number, rho: number, halfW: number, halfH: number): number {
  const cos = Math.cos(theta);
  const sin = Math.sin(theta);
  let lo = -Infinity;
  let hi = Infinity;

  if (Math.abs(sin) > 1e-6) {
    const a = (rho * cos + halfW) / sin;
    const b = (rho * cos - halfW) / sin;
    lo = Math.max(lo, Math.min(a, b));
    hi = Math.min(hi, Math.max(a, b));
  } else if (Math.abs(rho * cos) > halfW) {
    return 0;
  }

  if (Math.abs(cos) > 1e-6) {
    const a = (-halfH - rho * sin) / cos;
    const b = (halfH - rho * sin) / cos;
    lo = Math.max(lo, Math.min(a, b));
    hi = Math.min(hi, Math.max(a, b));
  } else if (Math.abs(rho * sin) > halfH) {
    return 0;
  }

  return Math.max(0, hi - lo);
}

/**
 * Of the lines running one way, the outermost two.
 *
 * Returned in a single convention. A line may be described either as pointing
 * one way at distance ρ or the other way at distance −ρ, and both descriptions
 * turn up in the same family whenever its heading straddles zero degrees —
 * a page held almost square to the camera, which is most of them. Left
 * un-normalised, one of a page's two opposite edges reads as being on the far
 * side of the frame from where it is, and the "outermost two" are then the same
 * edge twice.
 */
function outerPair(family: Line[], heading: number, minSpread: number): [Line, Line] | null {
  if (family.length < 2) return null;

  const normalised = family.map((line) => {
    // How many half-turns separate this line's stated heading from the
    // family's. An odd number means the two are describing the same direction
    // from opposite ends, and a distance measured from one end is the negative
    // of the distance measured from the other.
    //
    // Counting the half-turns is the whole point, and taking the angle modulo
    // 180 instead — which is the obvious way to write this — throws that count
    // away. It cost the detector every page held square to the camera: the top
    // edge got its sign corrected and the bottom edge did not, the two landed
    // within two pixels of each other, and a page filling the frame was
    // reported as no page at all.
    const turns = Math.round(((line.theta * 180) / Math.PI - heading) / 180);
    return {
      theta: line.theta - turns * Math.PI,
      rho: turns % 2 === 0 ? line.rho : -line.rho,
      votes: line.votes,
    };
  });

  const strongest = normalised.reduce((best, line) => (line.votes > best.votes ? line : best));
  // A faint line is usually the desk's own edge or a shadow; asking a candidate
  // to poll a third of what the page's clearest edge did keeps those out.
  const credible = normalised.filter((line) => line.votes >= strongest.votes * 0.34);
  if (credible.length < 2) return null;

  const sorted = credible.sort((a, b) => a.rho - b.rho);
  const first = sorted[0] as Line;
  const last = sorted[sorted.length - 1] as Line;
  if (last.rho - first.rho < minSpread) return null;
  return [first, last];
}

/**
 * The buffers a frame is worked out in, kept between frames.
 *
 * The viewfinder runs this ten times a second forever, and every frame wants
 * the same half-megabyte: two for the blur, three for the gradient, and the
 * accumulator, which is the large one. Allocating them per frame hands the
 * garbage collector half a megabyte of short-lived typed arrays a second, and
 * a collection that lands mid-frame is exactly the stutter the worker exists to
 * prevent. Every one of them is fully written before it is read — the border
 * the gradient skips is never read, and the accumulator is wiped — so nothing
 * of one frame survives into the next.
 */
let scratch: {
  width: number;
  height: number;
  pass: Uint8Array;
  blurred: Uint8Array;
  gradient: Gradient;
  accumulator: Int32Array;
} | null = null;

function workspace(width: number, height: number) {
  if (scratch && scratch.width === width && scratch.height === height) return scratch;
  const pixels = width * height;
  const rhoBins = Math.ceil(Math.hypot(width / 2, height / 2)) * 2 + 1;
  scratch = {
    width,
    height,
    pass: new Uint8Array(pixels),
    blurred: new Uint8Array(pixels),
    gradient: {
      magnitude: new Uint16Array(pixels),
      gx: new Int16Array(pixels),
      gy: new Int16Array(pixels),
    },
    accumulator: new Int32Array(THETA_STEPS * rhoBins),
  };
  return scratch;
}

/**
 * The page in this frame, or null if there is not one to be sure of.
 *
 * Null is a real answer and the common one — a moving hand, a frame half of
 * which is still the desk. The camera keeps the last outline it was sure of on
 * screen rather than flickering it away, so the cost of saying nothing is a
 * frame's staleness, and the cost of guessing is a scan cropped through the
 * middle of the text.
 */
export function detectPage(gray: Uint8Array, width: number, height: number): Quad | null {
  const work = workspace(width, height);
  const gradient = sobel(blur(gray, width, height, work.pass, work.blurred), width, height, work.gradient);
  const threshold = edgeThreshold(gradient.magnitude);
  const lines = houghLines(gradient, width, height, threshold, work.accumulator);
  if (lines.length < 4) return null;

  const heading = ((lines[0] as Line).theta * 180) / Math.PI;
  const across: Line[] = [];
  const along: Line[] = [];
  for (const line of lines) {
    const gap = angleGap((line.theta * 180) / Math.PI, heading);
    if (gap <= FAMILY_DEGREES) across.push(line);
    else if (gap >= 90 - FAMILY_DEGREES) along.push(line);
  }

  // Opposite edges of a page that fills a useful part of the frame are at least
  // this far apart; anything closer is one edge found twice.
  const spread = Math.min(width, height) * 0.22;
  const pairA = outerPair(across, heading, spread);
  const pairB = outerPair(along, heading + 90, spread);
  if (!pairA || !pairB) return null;

  const corners: Point[] = [];
  for (const a of pairA) {
    for (const b of pairB) {
      const hit = intersect(a, b);
      if (!hit) return null;
      corners.push({ x: hit.x + width / 2, y: hit.y + height / 2 });
    }
  }

  const quad = orderQuad(corners);
  return plausible(quad, width, height) ? quad : null;
}

/**
 * Whether a quadrilateral could be a photograph of a sheet of paper.
 *
 * Each test names a way the four strongest lines in a frame turn out not to be
 * a page: the desk's two edges crossing the frame's two, a picture frame on the
 * wall behind, a page detected so far off screen that its corners are somewhere
 * in the room. The detector reports nothing rather than any of these, and the
 * whole frame is scanned instead.
 */
function plausible(quad: Quad, width: number, height: number): boolean {
  if (quad.some((p) => !Number.isFinite(p.x) || !Number.isFinite(p.y))) return false;

  // Corners are allowed off-screen — a page held close runs past the frame —
  // but only just, and never far enough to be a line crossing somewhere else.
  const marginX = width * 0.25;
  const marginY = height * 0.25;
  if (quad.some((p) => p.x < -marginX || p.x > width + marginX)) return false;
  if (quad.some((p) => p.y < -marginY || p.y > height + marginY)) return false;

  if (!isConvex(quad)) return false;

  const area = quadArea(quad);
  if (area < width * height * 0.1) return false;
  if (area > width * height * 1.8) return false;

  const shortest = Math.min(
    ...quad.map((p, i) => {
      const next = quad[(i + 1) % 4] as Point;
      return Math.hypot(next.x - p.x, next.y - p.y);
    }),
  );
  if (shortest < Math.min(width, height) * 0.14) return false;

  // A rectangle seen from an angle keeps its corners recognisably square; a
  // trapezoid with a 20° corner is two unrelated lines that happened to cross.
  for (let i = 0; i < 4; i += 1) {
    const prev = quad[(i + 3) % 4] as Point;
    const here = quad[i] as Point;
    const next = quad[(i + 1) % 4] as Point;
    const ax = prev.x - here.x;
    const ay = prev.y - here.y;
    const bx = next.x - here.x;
    const by = next.y - here.y;
    const cosine = (ax * bx + ay * by) / (Math.hypot(ax, ay) * Math.hypot(bx, by));
    const degrees = (Math.acos(Math.max(-1, Math.min(1, cosine))) * 180) / Math.PI;
    if (degrees < 45 || degrees > 135) return false;
  }

  return true;
}